import {
	BottomSheetFooter,
	type BottomSheetFooterProps,
} from "@gorhom/bottom-sheet";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useCallback } from "react";
import { ScrollView } from "react-native";
import { Button as PaperButton, Text } from "react-native-paper";
import type { LevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import BottomSheetModalWrapper from "../BottomSheetModalWrapper";
import Box from "../Box";
import { type ConnectedLevel, ConnectedLevels } from "../ConnectedLevels";
import { InsightCallout } from "../DataCards/InsightCallout";
import DialogWrapper from "../DialogWrapper";
import { LevelIcon } from "../LevelIcon";
import { RecommendedSessionCard } from "../RecommendedSessionCard";
import { Skeleton } from "../Skeleton";
import { StatTile } from "../StatTile";
import type { ItemTypeSession, ItemTypeStat } from "./types";

export type ItemTypeSessionSection = {
	sessionsLabel: string;
	sessions: ItemTypeSession[];
	selectedSessionId?: string;
	onSelectSession: (id: string) => void;
	insight?: string;
	startLabel: string;
	onStart: () => void;
	startDisabled?: boolean;
	starting?: boolean;
	loading?: boolean;
};

type Props = {
	visible: boolean;
	onClose: () => void;
	icon?: LevelIconName;
	title: string;
	description?: string;
	levels: ConnectedLevel[];
	stats: ItemTypeStat[];
	showLevelLabels?: boolean;
	sessionSection?: ItemTypeSessionSection;
};

export type ItemTypeDetailModalProps = Props;

const DIALOG_MAX_WIDTH = 560;
const LEVELS_MAX_WIDTH = 400;
const SHEET_SNAP_POINT = "90%";
const SHEET_FOOTER_CLEARANCE = 220;

const Header = ({
	icon,
	title,
	description,
	levels,
	stats,
	showLevelLabels,
}: Pick<
	Props,
	"icon" | "title" | "description" | "levels" | "stats" | "showLevelLabels"
>) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	return (
		<Box gap={16} alignItems="center" width="100%">
			<Box gap={8} alignItems="center" width="100%">
				<Box gap={6} alignItems="center" width="100%">
					<LevelIcon
						name={icon ?? "card-text-outline"}
						size={24}
						color={theme.colors.onSurface}
					/>
					<Text
						variant="headlineSmall"
						style={{ color: theme.colors.onSurface, textAlign: "center" }}
					>
						{title}
					</Text>
				</Box>
				{description ? (
					<Text
						variant="bodySmall"
						style={{ color: theme.colors.onSurface, textAlign: "center" }}
					>
						{description}
					</Text>
				) : null}
			</Box>
			{levels.length > 0 ? (
				<Box width="100%" maxWidth={LEVELS_MAX_WIDTH} alignSelf="center">
					<ConnectedLevels
						levels={levels}
						size="md"
						showLabels={showLevelLabels}
					/>
				</Box>
			) : null}
			<Box flexDirection="row" gap={isMobile ? 4 : 8} width="100%">
				{stats.map((stat) => (
					<Box key={stat.label} flex={1} style={{ flexBasis: 0 }}>
						<StatTile
							icon={stat.icon}
							value={stat.value}
							label={stat.label}
							tooltip={stat.tooltip}
						/>
					</Box>
				))}
			</Box>
		</Box>
	);
};

const SessionsSection = ({
	sessionsLabel,
	sessions,
	selectedSessionId,
	onSelectSession,
	starting,
	loading,
}: Pick<
	ItemTypeSessionSection,
	| "sessionsLabel"
	| "sessions"
	| "selectedSessionId"
	| "onSelectSession"
	| "starting"
	| "loading"
>) => {
	const theme = useAppTheme();

	return (
		<Box gap={16} width="100%">
			<Text variant="titleSmall" style={{ color: theme.colors.onSurface }}>
				{sessionsLabel}
			</Text>
			<Box gap={16} width="100%">
				{loading ? (
					<>
						<Skeleton width="100%" height={76} />
						<Skeleton width="100%" height={76} />
					</>
				) : (
					sessions.map((session) => (
						<RecommendedSessionCard
							key={session.id}
							icon={session.icon}
							title={session.title}
							subtitle={
								<>
									<Text variant="titleSmall">{session.itemsCount}</Text>
									{` ${session.itemsLabel}`}
								</>
							}
							chips={session.tags}
							showUpgrade={session.showUpgrade}
							selected={session.id === selectedSessionId}
							disabled={starting}
							onPress={() => onSelectSession(session.id)}
						/>
					))
				)}
			</Box>
		</Box>
	);
};

const SessionBar = ({
	insight,
	startLabel,
	onStart,
	startDisabled,
	starting,
}: Pick<
	ItemTypeSessionSection,
	"insight" | "startLabel" | "onStart" | "startDisabled" | "starting"
>) => {
	return (
		<Box gap={16} width="100%">
			{insight ? <InsightCallout text={insight} variant="flat" /> : null}
			<PaperButton
				mode="contained"
				onPress={onStart}
				loading={starting}
				disabled={startDisabled || starting}
				icon={({ size, color }) => (
					<Icon name="play-circle-outline" size={size} color={color} />
				)}
			>
				{startLabel}
			</PaperButton>
		</Box>
	);
};

const ItemTypeDetailSheet = (props: Props) => {
	const { visible, onClose, sessionSection, ...headerProps } = props;
	const theme = useAppTheme();

	const renderFooter = useCallback(
		(footerProps: BottomSheetFooterProps) =>
			sessionSection ? (
				<BottomSheetFooter {...footerProps}>
					<Box
						paddingHorizontal={16}
						paddingTop={16}
						paddingBottom={16}
						borderTopWidth={1}
						borderTopColor={theme.colors.outlineVariant}
						backgroundColor={theme.colors.surface}
					>
						<SessionBar
							insight={sessionSection.insight}
							startLabel={sessionSection.startLabel}
							onStart={sessionSection.onStart}
							startDisabled={sessionSection.startDisabled}
							starting={sessionSection.starting}
						/>
					</Box>
				</BottomSheetFooter>
			) : null,
		[sessionSection, theme.colors.outlineVariant, theme.colors.surface],
	);

	return (
		<BottomSheetModalWrapper
			showModal={visible}
			snapIndex={0}
			snapPoint={SHEET_SNAP_POINT}
			animateOnMount
			enablePanDownToClose
			enableDismissOnClose
			onDismiss={onClose}
			backgroundStyle={{ backgroundColor: theme.colors.surface }}
			footerComponent={renderFooter}
			contentContainerStyle={{
				gap: 16,
				paddingBottom: sessionSection ? SHEET_FOOTER_CLEARANCE : 32,
			}}
		>
			<Header {...headerProps} />
			{sessionSection ? (
				<SessionsSection
					sessionsLabel={sessionSection.sessionsLabel}
					sessions={sessionSection.sessions}
					selectedSessionId={sessionSection.selectedSessionId}
					onSelectSession={sessionSection.onSelectSession}
					starting={sessionSection.starting}
					loading={sessionSection.loading}
				/>
			) : null}
		</BottomSheetModalWrapper>
	);
};

export const ItemTypeDetailModal = (props: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	if (isMobile) {
		return <ItemTypeDetailSheet {...props} />;
	}

	const { visible, onClose, sessionSection, ...headerProps } = props;

	return (
		<DialogWrapper
			visible={visible}
			handleClose={onClose}
			showCloseButton
			maxWidth={DIALOG_MAX_WIDTH}
			width="100%"
		>
			<ScrollView
				showsVerticalScrollIndicator={false}
				contentContainerStyle={{
					paddingTop: 24,
					paddingBottom: 16,
					paddingHorizontal: 16,
					gap: 16,
				}}
			>
				<Header {...headerProps} />
				{sessionSection ? (
					<SessionsSection
						sessionsLabel={sessionSection.sessionsLabel}
						sessions={sessionSection.sessions}
						selectedSessionId={sessionSection.selectedSessionId}
						onSelectSession={sessionSection.onSelectSession}
						starting={sessionSection.starting}
						loading={sessionSection.loading}
					/>
				) : null}
			</ScrollView>
			{sessionSection ? (
				<Box
					borderTopWidth={1}
					borderColor={theme.colors.outlineVariant}
					paddingHorizontal={16}
					paddingTop={16}
					paddingBottom={16}
				>
					<SessionBar
						insight={sessionSection.insight}
						startLabel={sessionSection.startLabel}
						onStart={sessionSection.onStart}
						startDisabled={sessionSection.startDisabled}
						starting={sessionSection.starting}
					/>
				</Box>
			) : null}
		</DialogWrapper>
	);
};
